import { Box, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { atom, useAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { BasicDialog, IFormDialogProps } from '../../../../components/Dialog/BasicDialog';
import { BasicDragHandle, SortableItem } from '../../../../components/SortableList/SortableItem';
import { IItemWithId, SortableList } from '../../../../components/SortableList/SortableList';
import { CaptableSummaryColumnType } from '../../../../data-models/preferences-v3.data-model';
import {
  CaptableSummaryColumnLabels,
  captableSummaryPreferencesAtom,
  DefaultCaptableInvestmentColumnOrder,
} from './CaptableSummaryPreferences';

export const showCapTableSummaryGridConfigAtom = atom(false);

interface ISortableColumnItem extends IItemWithId {
  id: CaptableSummaryColumnType;
  label: string;
}

export function CaptableSummaryGridConfigDialog({ open, onClose }: IFormDialogProps) {
  const [prefs, setPrefs] = useAtom(captableSummaryPreferencesAtom);

  const onPinTotalToggle = useCallback(
    (_event: React.ChangeEvent, checked: boolean) => {
      setPrefs({
        ...prefs,
        pinTotalColumns: checked,
      });
    },
    [prefs, setPrefs]
  );

  const sortableItems: ISortableColumnItem[] = useMemo(() => {
    const columnOrder = prefs.investmentColumnOrder?.length
      ? prefs.investmentColumnOrder
      : DefaultCaptableInvestmentColumnOrder;

    return columnOrder.map((columnType) => ({
      id: columnType,
      label: CaptableSummaryColumnLabels[columnType],
    }));
  }, [prefs.investmentColumnOrder]);

  const onColumnOrderChange = useCallback(
    (reorderedItems: ISortableColumnItem[]) => {
      const newColumnOrder = reorderedItems.map((item) => item.id);
      setPrefs({
        ...prefs,
        investmentColumnOrder: newColumnOrder,
      });
    },
    [prefs, setPrefs]
  );

  const renderColumnItem = useCallback((item: ISortableColumnItem) => {
    return (
      <SortableItem id={item.id}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: '0.25rem 0.25rem',
            gap: '1rem',
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <BasicDragHandle />
          <Typography variant='body2' sx={{ flex: 1 }}>
            {item.label}
          </Typography>
        </Box>
      </SortableItem>
    );
  }, []);

  return (
    <BasicDialog
      open={open}
      onClose={onClose}
      titleComponent={<Typography variant='subtitle1'>Grid Configuration</Typography>}
      maxWidth='sm'
      fullWidth
      lightDismiss
    >
      <Box sx={{ padding: '0 2.5rem 2rem' }}>
        <Stack spacing={10}>
          <Box>
            <FormControlLabel
              control={
                <Switch checked={prefs?.pinTotalColumns ?? false} onChange={onPinTotalToggle} size='small' />
              }
              label={<Typography variant='body2'>Pin Total Columns</Typography>}
            />
          </Box>

          <Box>
            <Typography variant='body2' component='h3' sx={{ mb: 5 }}>
              Column Order
            </Typography>
            <SortableList
              items={sortableItems}
              onChange={onColumnOrderChange}
              renderItem={renderColumnItem}
            />
          </Box>
        </Stack>
      </Box>
    </BasicDialog>
  );
}
